import React from 'react';
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LightbulbIcon, ArrowRight, PenTool, ClipboardList, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CopilotWarningDialogProps {
    onClose: () => void;
    data: {
        projectId: string;
        chapterId: string;
        beatsCount: number;
        hasOutline: boolean;
        onWriteOutline: () => void;
        onPlanChapter: () => void;
    };
}

export const CopilotWarningDialog: React.FC<CopilotWarningDialogProps> = ({ onClose, data }) => {
    const { beatsCount, hasOutline, onWriteOutline, onPlanChapter } = data;

    return (
        <DialogContent className="sm:max-w-[480px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0 bg-background">
            <DialogHeader className="px-6 py-6 pb-5 border-b border-border/40 bg-muted/5 space-y-1">
                <div className="flex items-center gap-2.5 mb-1 text-amber-500">
                    <AlertCircle className="h-5 w-5" />
                    <DialogTitle className="text-xl font-semibold text-foreground tracking-tight">
                        More Context Needed
                    </DialogTitle>
                </div>
                <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
                    Personalize AI suggestions by providing more structural details.
                </DialogDescription>
            </DialogHeader>

            <div className="px-6 py-6 space-y-4">
                {/* Status Evaluation Cards with Inline Actions */}
                <div className="grid gap-3">
                    {/* Beats Card */}
                    <div className={cn(
                        "flex items-center justify-between gap-4 p-4 rounded-xl border transition-all",
                        beatsCount < 2 
                            ? "bg-muted/30 border-border/60" 
                            : "bg-emerald-500/[0.03] border-emerald-500/10"
                    )}>
                        <div className="flex items-center gap-3.5 min-w-0">
                            <div className={cn(
                                "flex-shrink-0 p-2 rounded-lg border",
                                beatsCount < 2 ? "bg-background border-border text-muted-foreground" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"
                            )}>
                                <ClipboardList className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[11px] font-bold uppercase tracking-wider mb-0.5 opacity-60">Chapter Beats</h4>
                                <p className={cn(
                                    "text-xs leading-normal font-medium truncate",
                                    beatsCount < 2 ? "text-foreground/70" : "text-emerald-600"
                                )}>
                                    {beatsCount} of 2 beats found
                                </p>
                            </div>
                        </div>
                        
                        {beatsCount < 2 && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    onClose();
                                    onPlanChapter();
                                }}
                                className="h-8 rounded-lg text-[11px] px-3 font-semibold bg-background border-border/60 hover:bg-muted/80 transition-all flex-shrink-0"
                            >
                                Plan Scenes
                                <ArrowRight className="h-3 w-3 ml-1.5 opacity-60" />
                            </Button>
                        )}
                    </div>

                    {/* Outline Card */}
                    <div className={cn(
                        "flex items-center justify-between gap-4 p-4 rounded-xl border transition-all",
                        !hasOutline 
                            ? "bg-muted/30 border-border/60" 
                            : "bg-emerald-500/[0.03] border-emerald-500/10"
                    )}>
                        <div className="flex items-center gap-3.5 min-w-0">
                            <div className={cn(
                                "flex-shrink-0 p-2 rounded-lg border",
                                !hasOutline ? "bg-background border-border text-muted-foreground" : "bg-emerald-500/10 border-emerald-500/10 text-emerald-600"
                            )}>
                                <PenTool className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[11px] font-bold uppercase tracking-wider mb-0.5 opacity-60">Chapter Outline</h4>
                                <p className={cn(
                                    "text-xs leading-normal font-medium truncate",
                                    !hasOutline ? "text-foreground/70" : "text-emerald-600"
                                )}>
                                    {hasOutline ? "Outline completed" : "No outline found"}
                                </p>
                            </div>
                        </div>

                        {!hasOutline && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    onClose();
                                    onWriteOutline();
                                }}
                                className="h-8 rounded-lg text-[11px] px-3 font-semibold bg-background border-border/60 hover:bg-muted/80 transition-all flex-shrink-0"
                            >
                                <PenTool className="h-3 w-3 mr-1.5 opacity-60" />
                                Add Outline
                            </Button>
                        )}
                    </div>
                </div>

                {/* Subtle Tip Box */}
                <div className="px-4 py-3 rounded-xl bg-primary/[0.02] border border-primary/10 flex items-start gap-3">
                    <LightbulbIcon className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                        <span className="font-bold text-primary mr-1">Pro Tip:</span>
                        High-quality context results in much better AI suggestions and more consistent prose style.
                    </p>
                </div>
            </div>

            <DialogFooter className="px-6 py-4 bg-muted/10 border-t border-border/40 flex items-center justify-end">
                <Button
                    variant="ghost"
                    onClick={onClose}
                    className="h-9 px-4 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                    Maybe Later
                </Button>
            </DialogFooter>
        </DialogContent>
    );
};
